import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowLeft, BookMarked, Quote, Lightbulb, Bookmark, Repeat, Trash2, Plus, Lock, X, Sparkles, ArrowRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import '../styles/MyStory.css';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch (err) {
    return '';
  }
}

function ChapterDetail({ chapter, onClose, onDelete }) {
  return (
    <motion.div
      className="ms-detail-overlay"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      data-testid="ms-detail-overlay"
    >
      <motion.article
        className="ms-detail"
        initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        role="dialog" aria-modal="true"
      >
        <button type="button" className="ms-detail-close" onClick={onClose} aria-label="Close" data-testid="ms-detail-close">
          <X size={18} />
        </button>
        <header className="ms-detail-head">
          <p className="ms-detail-date">{formatDate(chapter.timestamp)}</p>
          <h2 className="ms-detail-title">{chapter.book_title}</h2>
          {chapter.book_author && <p className="ms-detail-author">by {chapter.book_author}</p>}
        </header>
        <span className="ms-divider" aria-hidden="true">
          <span className="ms-divider-line" /><span className="ms-divider-mark">❦</span><span className="ms-divider-line" />
        </span>

        <section className="ms-detail-section">
          <div className="ms-detail-section-head"><Quote size={16} /><h3>My Reflection</h3></div>
          <p className="ms-detail-text">{chapter.reflection}</p>
        </section>
        {chapter.ideas_to_practice && (
          <section className="ms-detail-section">
            <div className="ms-detail-section-head"><Lightbulb size={16} /><h3>Ideas to Practice</h3></div>
            <p className="ms-detail-text">{chapter.ideas_to_practice}</p>
          </section>
        )}
        {chapter.passage_to_remember && (
          <section className="ms-detail-section">
            <div className="ms-detail-section-head"><Bookmark size={16} /><h3>A Passage to Remember</h3></div>
            <p className="ms-detail-text">{chapter.passage_to_remember}</p>
          </section>
        )}
        {chapter.revisit_later && (
          <section className="ms-detail-section">
            <div className="ms-detail-section-head"><Repeat size={16} /><h3>Revisit Later</h3></div>
            <p className="ms-detail-text">{chapter.revisit_later}</p>
          </section>
        )}

        <footer className="ms-detail-foot">
          <button
            type="button"
            className="ms-delete-btn"
            onClick={() => onDelete(chapter.id)}
            data-testid={`ms-delete-${chapter.id}`}
          >
            <Trash2 size={15} /> Remove this chapter
          </button>
        </footer>
      </motion.article>
    </motion.div>
  );
}

export default function MyStoryPage({ embedded = false } = {}) {
  const { isAuthenticated, user, loading, authHeaders } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null); // { chapters, count, limit, plan, at_cap }
  const [crafting, setCrafting] = useState(null); // { active: [...] }
  const [fetching, setFetching] = useState(true);
  const [err, setErr] = useState('');
  const [selected, setSelected] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  // Redirect to login if not authenticated (after auth has hydrated)
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login', { replace: true, state: { from: '/my-story' } });
    }
  }, [loading, isAuthenticated, navigate]);

  const loadChapters = useCallback(async () => {
    if (!isAuthenticated) return;
    setFetching(true); setErr('');
    try {
      const res = await fetch(`${API}/user/chapters?limit=200`, { headers: authHeaders() });
      if (!res.ok) throw new Error('failed');
      const json = await res.json();
      setData(json);
    } catch (err) {
      setErr('Could not load your chapters. Please refresh.');
    } finally {
      setFetching(false);
    }
  }, [isAuthenticated, authHeaders]);

  useEffect(() => { loadChapters(); }, [loadChapters]);
  const [siddhis, setSiddhis] = useState([]);
  useEffect(() => {
    if (!isAuthenticated) return;
    fetch(`${API}/mycraftz-install/my-siddhis`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : { siddhis: [] }))
      .then((j) => setSiddhis(j.siddhis || []))
      .catch(() => {});
  }, [isAuthenticated, authHeaders]);
  useEffect(() => {
    window.addEventListener('icraftz:chapter-captured', loadChapters);
    return () => window.removeEventListener('icraftz:chapter-captured', loadChapters);
  }, [loadChapters]);

  // Quiet bridge to MyCraftz™ — fetch active CraftPrintz for the ribbon.
  useEffect(() => {
    if (!isAuthenticated) return;
    let alive = true;
    // Real (Sankalpa-sealed) practices only — legacy installs live on MyCraftz.
    fetch(`${API}/mycraftz-install/my-craftprints`, { headers: authHeaders() })
      .then((r) => r.ok ? r.json() : null)
      .then((j) => {
        if (!alive || !j) return;
        const active = (j.craftprints || []).map((c) => ({
          book_id: c.book_id, book_title: c.book_title,
          idea_name: `${(c.ideas || []).length} practices`,
          days_completed: Math.max(0, ...(c.ideas || []).map((i) => i.days_kept)),
          duration_days: 30,
        }));
        setCrafting({ active });
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [isAuthenticated, authHeaders]);

  const handleDelete = async (id) => {
    setConfirmDeleteId(null);
    try {
      const res = await fetch(`${API}/user/chapters/${id}`, { method: 'DELETE', headers: authHeaders() });
      if (!res.ok) throw new Error('failed');
      setSelected(null);
      await loadChapters();
    } catch (err) {
      setErr('Could not remove that chapter. Please try again.');
    }
  };

  if (loading || !isAuthenticated) {
    return (
      <div className="ms-page" data-testid="ms-page">
        <p className="ms-loading">Opening your library…</p>
      </div>
    );
  }

  const chapters = data?.chapters || [];
  // One story timeline: Chapters (what moved you) and Siddhis (what happened
  // when you practised it), newest first.
  const timeline = [
    ...chapters.map((c) => ({ kind: "chapter", at: c.timestamp, c })),
    ...siddhis.map((x) => ({ kind: "siddhi", at: x.generated_at, x })),
  ].sort((a, b) => String(b.at).localeCompare(String(a.at)));
  const count = data?.count ?? 0;
  const limit = data?.limit ?? null;
  const atCap = data?.at_cap === true;
  const remaining = limit != null ? Math.max(0, limit - count) : null;

  return (
    <div className="ms-page" data-testid="ms-page">
      {!embedded && (
        <Link to="/" className="ms-back" data-testid="ms-back">
          <ArrowLeft size={15} /> Back to iCraftz
        </Link>
      )}

      {/* MyCraftz™ — Currently Crafting ribbon. Subtle bridge, not a streak. */}
      {crafting && crafting.active.length > 0 ? (
        <Link
          to={(() => {
            if (crafting.active.length === 1) {
              return `/sadhana?book=${crafting.active[0].book_id}`;
            }
            return "/mycraftz";
          })()}
          className="ms-crafting-ribbon"
          data-testid="ms-crafting-ribbon"
        >
          <span className="ms-crafting-eyebrow">
            <Sparkles size={11} /> {crafting.active.length === 1 ? 'Currently Crafting' : `MyCraftz\u2122`}
          </span>
          {crafting.active.length === 1 ? (
            <span className="ms-crafting-body">
              <strong>{crafting.active[0].idea_name}</strong>
              <em> · from <i>{crafting.active[0].book_title}</i></em>
              <span className="ms-crafting-day">
                Day {crafting.active[0].days_completed} of {crafting.active[0].duration_days}
              </span>
            </span>
          ) : (
            <span className="ms-crafting-body">
              <strong>{crafting.active.length}</strong>
              <em> Active CraftPrintz™</em>
            </span>
          )}
          <span className="ms-crafting-cta">
            Continue Crafting <ArrowRight size={12} />
          </span>
        </Link>
      ) : null}

      <header className="ms-header">
        <p className="ms-eyebrow">Your private sanctuary</p>
        <h1 className="ms-title">My <span className="ms-title-accent">Sanctuary</span></h1>
        <p className="ms-sub">
          {chapters.length > 0
            ? `Welcome back, ${user?.name?.split(' ')[0] || 'reader'}. Every chapter that moved you, kept close.`
            : siddhis.length > 0
            ? `Welcome, ${user?.name?.split(' ')[0] || 'reader'}. Your shelf holds your first Siddhi — capture a chapter to keep it company.`
            : `Welcome, ${user?.name?.split(' ')[0] || 'reader'}. Your shelf awaits its first chapter.`}
        </p>

        {limit != null && (
          <div className="ms-usage" data-testid="ms-usage">
            <Lock size={13} />
            <span className="ms-usage-text">
              <strong>{count}</strong> of <strong>{limit}</strong> chapters sealed
              {remaining > 0 && <em> · {remaining} remaining on the free Arambh plan</em>}
              {atCap && <em className="ms-usage-cap"> · shelf full</em>}
            </span>
          </div>
        )}

        <span className="ms-divider" aria-hidden="true">
          <span className="ms-divider-line" /><span className="ms-divider-mark">❦</span><span className="ms-divider-line" />
        </span>
      </header>

      {err && <p className="ms-error" data-testid="ms-error" role="alert">{err}</p>}

      {fetching ? (
        <div className="ms-loading-wrap"><p className="ms-loading">Unfurling your shelves…</p></div>
      ) : timeline.length === 0 ? (
        <div className="ms-empty" data-testid="ms-empty">
          <BookMarked size={28} />
          <h2 className="ms-empty-heading">Your library is quiet.</h2>
          <p className="ms-empty-body">
            Capture your first chapter — a reflection on a book that moved you — and it will live here always.
          </p>
          <Link to="/#chapters-within" className="ms-empty-cta" data-testid="ms-empty-cta">
            <Plus size={15} /> Capture a chapter
          </Link>
        </div>
      ) : (
        <>
          <ul className="ms-grid" data-testid="ms-grid">
            {timeline.map(({ kind, c, x }) => kind === "siddhi" ? (
              <li key={`siddhi-${x.book_id}-${x.lang}`}>
                <Link to={`/siddhi?book=${x.book_id}`} className="ms-card ms-card-siddhi" data-testid={`ms-siddhi-${x.book_id}-${x.lang}`}>
                  <header className="ms-card-head">
                    <span className="ms-card-date">{formatDate(x.generated_at)}</span>
                    <Sparkles size={15} className="ms-card-icon" />
                  </header>
                  <h3 className="ms-card-title">{x.book_title}</h3>
                  <p className="ms-card-author">Siddhi — what happened when you practised it</p>
                  <p className="ms-card-excerpt">{x.identity_statement}</p>
                </Link>
              </li>
            ) : (
              <li key={c.id}>
                <button
                  type="button"
                  className="ms-card"
                  onClick={() => setSelected(c)}
                  data-testid={`ms-card-${c.id}`}
                >
                  <header className="ms-card-head">
                    <span className="ms-card-date">{formatDate(c.timestamp)}</span>
                    <BookMarked size={15} className="ms-card-icon" />
                  </header>
                  <h3 className="ms-card-title">{c.book_title}</h3>
                  {c.book_author && <p className="ms-card-author">by {c.book_author}</p>}
                  <p className="ms-card-excerpt">{c.reflection}</p>
                  <footer className="ms-card-foot">
                    <span className="ms-card-tag">
                      <Quote size={11} /> {c.language_label || 'English'}
                    </span>
                  </footer>
                </button>
              </li>
            ))}
            <li>
              <Link
                to={atCap ? '/#chapters-within' : '/#chapters-within'}
                className={`ms-card ms-card-add${atCap ? ' is-cap' : ''}`}
                data-testid="ms-card-add"
              >
                <Plus size={32} strokeWidth={1.6} />
                <p className="ms-card-add-label">
                  {atCap ? 'Free a slot to capture more' : 'Capture another chapter'}
                </p>
                {atCap && (
                  <p className="ms-card-add-sub">Remove one to add a new chapter on the free plan.</p>
                )}
              </Link>
            </li>
          </ul>
        </>
      )}

      <AnimatePresence>
        {selected && (
          <ChapterDetail
            chapter={selected}
            onClose={() => setSelected(null)}
            onDelete={(id) => setConfirmDeleteId(id)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {confirmDeleteId && (
          <motion.div
            className="ms-confirm-overlay"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setConfirmDeleteId(null)}
            data-testid="ms-confirm-overlay"
          >
            <motion.div
              className="ms-confirm"
              initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 10, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3>Remove this chapter?</h3>
              <p>This can't be undone. Your reflection will be gone forever from your library.</p>
              <div className="ms-confirm-actions">
                <button
                  type="button"
                  className="ms-confirm-cancel"
                  onClick={() => setConfirmDeleteId(null)}
                  data-testid="ms-confirm-cancel"
                >
                  Keep it
                </button>
                <button
                  type="button"
                  className="ms-confirm-delete"
                  onClick={() => handleDelete(confirmDeleteId)}
                  data-testid="ms-confirm-delete"
                >
                  Remove
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
